import { FC } from "react";
import styled from "@emotion/styled";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";

/* The mark is white-on-brand by logo definition; theme onAccent tokens
   invert in dark mode and would repaint it near-black. */
const MEM_AVATAR_MARK_COLOR = "#FEFEFE";

/**
 * Persistent "N replies" link on a threaded root message, rendered inside
 * the message's actions row (Slack model): avatars for the participants who
 * authored replies, followed by the brand-colored reply count.
 */
export const MDSSessionThreadRow: FC<{
  replyCount: number;
  onOpenThread: () => void;
  /** Render the user participant's avatar (they authored a reply). */
  showUserAvatar?: boolean;
  /** Render Mem's round avatar (it authored a reply). */
  showAgentAvatar?: boolean;
  userPhotoUrl?: string | null;
}> = ({ replyCount, onOpenThread, showUserAvatar, showAgentAvatar, userPhotoUrl }) => {
  return (
    <StyledThreadLink onClick={onOpenThread}>
      {(showUserAvatar || showAgentAvatar) && (
        <StyledParticipants aria-hidden="true">
          {showUserAvatar && userPhotoUrl && <StyledParticipantPhoto src={userPhotoUrl} alt="" />}
          {showAgentAvatar && (
            <StyledMemAvatar>
              <MDSIcon
                kind={MDSIconKind.Logo}
                boxSize={12}
                size={12}
                overrideColor={MEM_AVATAR_MARK_COLOR}
              />
            </StyledMemAvatar>
          )}
        </StyledParticipants>
      )}
      {replyCount === 1 ? "1 reply" : `${replyCount} replies`}
    </StyledThreadLink>
  );
};

const StyledThreadLink = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  border: none;
  background: transparent;
  padding: 0 2px;
  cursor: pointer;
  user-select: none;
  color: ${({ theme }) => theme.colors.icon.brand};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const StyledParticipants = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const StyledParticipantPhoto = styled.img`
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
`;

/* Mem's avatar: the round brand circle with the white mark, miniature of
   the floating circle. */
const StyledMemAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.icon.brand};
`;
